import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Download, FileBarChart2, Layers } from "lucide-react";
import SiteSelector, { useSiteSelector } from "../components/SiteSelector";
import { toast } from "sonner";

const fmtLocal = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

export default function Reports() {
  const { sites, siteId, setSiteId } = useSiteSelector();
  const [scope, setScope] = useState("single");   // "single" | "all"
  const [tags, setTags] = useState([]);
  const [tagId, setTagId] = useState("");
  const [startDt, setStartDt] = useState(fmtLocal(new Date(Date.now() - 60 * 60 * 1000)));
  const [endDt, setEndDt] = useState(fmtLocal(new Date()));
  const [rows, setRows] = useState([]);          // single-tag rows
  const [allData, setAllData] = useState(null);  // all-tags matrix response
  const [tag, setTag] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    api.get("/tags", { params: { site_id: siteId } }).then(({ data }) => {
      setTags(data);
      if (data[0]) setTagId(data[0].id);
    });
    setRows([]); setAllData(null);
  }, [siteId]);

  const setPreset = (mins) => {
    const end = new Date();
    const start = new Date(Date.now() - mins * 60 * 1000);
    setStartDt(fmtLocal(start)); setEndDt(fmtLocal(end));
  };

  const run = async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const params = { start: new Date(startDt).toISOString(), end: new Date(endDt).toISOString() };
      if (scope === "single") {
        if (!tagId) return;
        const { data } = await api.get(`/tags/${tagId}/history`, { params });
        setTag(data.tag); setRows(data.readings); setAllData(null);
      } else {
        const { data } = await api.get("/reports/all_tags", { params: { site_id: siteId, ...params } });
        setAllData(data); setRows([]);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const singleStats = rows.length ? {
    count: rows.length,
    min: Math.min(...rows.map(r => r.value)).toFixed(2),
    max: Math.max(...rows.map(r => r.value)).toFixed(2),
    avg: (rows.reduce((a, r) => a + r.value, 0) / rows.length).toFixed(2),
  } : null;

  const download = () => {
    if (scope === "single") {
      if (!rows.length) return;
      const header = `# Report — ${tag?.name}\n# Range: ${startDt} → ${endDt}\n# Unit: ${tag?.unit || ""}\ntimestamp,value,status,unit\n`;
      const csv = header + rows.map(r => `${r.ts},${r.value},${r.status},${tag?.unit || ""}`).join("\n");
      dlBlob(csv, `${tag?.name?.replace(/\s+/g, "_")}_${startDt}_${endDt}.csv`);
    } else {
      if (!allData || !allData.rows.length) return;
      const tagCols = allData.tags.map(t => `${t.name}${t.unit ? ` (${t.unit})` : ""}`);
      const tagIds = allData.tags.map(t => t.id);
      const header = `# Consolidated Report — ${sites.find(s => s.id === siteId)?.name}\n# Range: ${startDt} → ${endDt}\n# Tags: ${allData.tags.length}\ntimestamp,${tagCols.join(",")}\n`;
      const csv = header + allData.rows.map(row => {
        const vals = tagIds.map(tid => row[tid] ?? "");
        return `${row.ts},${vals.join(",")}`;
      }).join("\n");
      dlBlob(csv, `all_tags_${startDt}_${endDt}.csv`.replace(/:/g, "-"));
    }
  };

  const dlBlob = (csv, name) => {
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name.replace(/:/g, "-"); a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="scada-label">DATA EXPORT</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Reports</h1>
        </div>
        <SiteSelector sites={sites} siteId={siteId} setSiteId={setSiteId} />
      </div>

      <div className="scada-panel p-5 space-y-4">
        <div className="flex gap-1" data-testid="report-scope">
          {[["single", "SINGLE TAG"], ["all", "ALL TAGS (CONSOLIDATED)"]].map(([k, l]) => (
            <button key={k} onClick={() => setScope(k)} data-testid={`scope-${k}`}
              className={`px-4 py-2 border font-mono-metric text-xs uppercase tracking-wider ${scope === k ? "border-[#00E5FF] text-[#00E5FF]" : "border-[#2C3238] text-[#9BA4B5]"}`}>
              {k === "all" && <Layers className="inline mr-1" size={12} />}{l}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {scope === "single" && (
            <div>
              <div className="scada-label mb-1">TAG</div>
              <select value={tagId} onChange={(e) => setTagId(e.target.value)} data-testid="report-tag-select" className="border px-3 py-2 text-sm font-mono-metric w-full">
                {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <div className="scada-label mb-1">START DATE/TIME</div>
            <input type="datetime-local" value={startDt} onChange={(e) => setStartDt(e.target.value)} data-testid="report-start" className="border px-3 py-2 text-sm font-mono-metric w-full" />
          </div>
          <div>
            <div className="scada-label mb-1">END DATE/TIME</div>
            <input type="datetime-local" value={endDt} onChange={(e) => setEndDt(e.target.value)} data-testid="report-end" className="border px-3 py-2 text-sm font-mono-metric w-full" />
          </div>
          <div className="flex items-end gap-1">
            <button onClick={run} disabled={loading} data-testid="report-generate" className="border border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 px-4 py-2 font-mono-metric uppercase tracking-wider text-xs disabled:opacity-50 flex-1">{loading ? "…" : "GENERATE"}</button>
            <button onClick={download} disabled={(scope === "single" ? !rows.length : !(allData && allData.rows.length))} data-testid="report-download" className="border border-[#2C3238] text-[#9BA4B5] hover:text-[#E3E8EE] hover:border-[#E3E8EE] px-3 py-2 font-mono-metric uppercase tracking-wider text-xs disabled:opacity-30"><Download size={12} /></button>
          </div>
        </div>
        <div className="flex gap-1 flex-wrap" data-testid="report-presets">
          {[["LAST 15M", 15], ["LAST 1H", 60], ["LAST 4H", 240], ["LAST 12H", 720], ["LAST 24H", 1440]].map(([lbl, m]) => (
            <button key={m} onClick={() => setPreset(m)} className="border border-[#2C3238] text-[#9BA4B5] hover:text-[#00E5FF] hover:border-[#00E5FF] px-3 py-1.5 font-mono-metric uppercase tracking-wider text-[10px]">{lbl}</button>
          ))}
        </div>
      </div>

      {scope === "single" && singleStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
          {[["SAMPLES", singleStats.count], ["MIN", `${singleStats.min}${tag?.unit || ""}`], ["MAX", `${singleStats.max}${tag?.unit || ""}`], ["AVG", `${singleStats.avg}${tag?.unit || ""}`]].map(([l, v]) => (
            <div key={l} className="scada-panel p-4"><div className="scada-label">{l}</div><div className="font-mono-metric text-2xl text-[#E3E8EE] mt-1">{v}</div></div>
          ))}
        </div>
      )}

      {scope === "all" && allData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
          <div className="scada-panel p-4"><div className="scada-label">TAGS</div><div className="font-mono-metric text-2xl text-[#E3E8EE] mt-1">{allData.tags.length}</div></div>
          <div className="scada-panel p-4"><div className="scada-label">SAMPLE ROWS</div><div className="font-mono-metric text-2xl text-[#E3E8EE] mt-1">{allData.count}</div></div>
          <div className="scada-panel p-4"><div className="scada-label">RANGE</div><div className="font-mono-metric text-sm text-[#E3E8EE] mt-1">{startDt.replace("T"," ")} → {endDt.replace("T"," ")}</div></div>
          <div className="scada-panel p-4"><div className="scada-label">FORMAT</div><div className="font-mono-metric text-sm text-[#00E5FF] mt-1">wide-CSV<br/>1 col per tag</div></div>
        </div>
      )}

      <div className="scada-panel overflow-x-auto">
        {scope === "single" ? (
          <div className="min-w-[600px]">
            <div className="grid grid-cols-12 px-4 py-3 border-b border-[#2C3238] scada-label sticky top-0 bg-[#14171A]">
              <div className="col-span-5">TIMESTAMP</div>
              <div className="col-span-3">VALUE</div>
              <div className="col-span-2">UNIT</div>
              <div className="col-span-2">STATUS</div>
            </div>
            {rows.length === 0 && <div className="px-4 py-10 text-center text-[#5A6573] font-mono-metric text-sm"><FileBarChart2 className="inline mr-2" size={14} /> Pick a date range and click GENERATE</div>}
            {rows.slice(-500).reverse().map((r, i) => (
              <div key={i} className="grid grid-cols-12 px-4 py-2 border-b border-[#2C3238]/60 text-xs font-mono-metric hover:bg-[#1E2226]/40">
                <div className="col-span-5 text-[#9BA4B5]">{new Date(r.ts).toLocaleString()}</div>
                <div className="col-span-3 text-[#E3E8EE]">{r.value}</div>
                <div className="col-span-2 text-[#9BA4B5]">{tag?.unit}</div>
                <div className="col-span-2 uppercase" style={{ color: r.status === "critical" ? "#FF3B30" : r.status === "warning" ? "#FFB300" : "#00E676" }}>{r.status}</div>
              </div>
            ))}
            {rows.length > 500 && <div className="px-4 py-2 text-center text-[#5A6573] font-mono-metric text-[10px]">Showing latest 500 of {rows.length}. Download CSV for full export.</div>}
          </div>
        ) : (
          <div>
            {!allData && <div className="px-4 py-10 text-center text-[#5A6573] font-mono-metric text-sm"><Layers className="inline mr-2" size={14} /> Choose a date range and click GENERATE to build a consolidated report</div>}
            {allData && (
              <table className="min-w-full text-xs font-mono-metric">
                <thead className="scada-label border-b border-[#2C3238] sticky top-0 bg-[#14171A]">
                  <tr>
                    <th className="text-left px-3 py-2">TIMESTAMP</th>
                    {allData.tags.map(t => <th key={t.id} className="text-left px-3 py-2 whitespace-nowrap">{t.name}{t.unit ? ` (${t.unit})` : ""}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {allData.rows.slice(-300).reverse().map((row, i) => (
                    <tr key={i} className="border-b border-[#2C3238]/60 hover:bg-[#1E2226]/40">
                      <td className="px-3 py-1.5 text-[#9BA4B5] whitespace-nowrap">{new Date(row.ts).toLocaleString()}</td>
                      {allData.tags.map(t => <td key={t.id} className="px-3 py-1.5 text-[#E3E8EE]">{row[t.id] ?? "—"}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {allData && allData.rows.length > 300 && <div className="px-4 py-2 text-center text-[#5A6573] font-mono-metric text-[10px]">Showing latest 300 of {allData.count}. Download CSV for full export.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
